import React, { ReactNode, useState } from "react";

export interface DiffDetail {
  type: "added" | "removed" | "modified";
  description?: string;
  changes?: string[];
}

export interface EndpointDiff {
  path: string;
  method: string;
  status: "added" | "removed" | "modified" | "unchanged";
  parameterDiffs?: DiffDetail[];
  requestBodyDiff?: DiffDetail;
  responseBodyDiff?: DiffDetail;
}

interface Props {
  diffData: {
    endpointDiffs: EndpointDiff[];
  };
}

interface CollapsibleProps {
  title: string;
  children: ReactNode;
}

const Badge: React.FC<{ type: string }> = ({ type }) => {
  const colorMap: Record<string, string> = {
    added: "bg-green-100 text-green-800",
    removed: "bg-red-100 text-red-800",
    modified: "bg-yellow-100 text-yellow-800",
    unchanged: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
        colorMap[type] || "bg-gray-100"
      }`}
    >
      {type.toUpperCase()}
    </span>
  );
};

const Collapsible = ({ title, children }: CollapsibleProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded mb-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 focus:outline-none"
      >
        <span className="font-medium">{title}</span>
        <svg
          className={`w-4 h-4 transform transition-transform ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && <div className="p-4 bg-white">{children}</div>}
    </div>
  );
};

export const OpenApiDiffRenderer: React.FC<Props> = ({ diffData }) => {
  if (!diffData.endpointDiffs.length) {
    return <p className="text-gray-500">No differences detected.</p>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {diffData.endpointDiffs.map((ep, idx) => (
        <div
          key={`${ep.method}-${ep.path}-${idx}`}
          className="mb-6 border rounded shadow-sm"
        >
          <div className="flex justify-between items-center px-5 py-3 bg-gray-100 border-b">
            <div>
              <span className="font-mono font-bold text-indigo-700">
                {ep.method.toUpperCase()}
              </span>{" "}
              <span className="text-lg font-semibold">{ep.path}</span>
            </div>
            <Badge type={ep.status} />
          </div>

          {(ep.status === "added" || ep.status === "removed") && (
            <p className="p-4 text-sm text-gray-700">
              Endpoint <strong>{ep.status}</strong> in one of the documents.
            </p>
          )}

          {ep.status === "modified" && (
            <div className="p-4 space-y-4">
              {/* Parameters */}
              {ep.parameterDiffs && ep.parameterDiffs.length > 0 && (
                <Collapsible title="Parameter Differences">
                  {ep.parameterDiffs.map((pd, i) => (
                    <p
                      key={i}
                      className={`text-sm ${
                        pd.type === "added"
                          ? "text-green-700"
                          : pd.type === "removed"
                          ? "text-red-700"
                          : "text-yellow-700"
                      }`}
                    >
                      {pd.description}
                    </p>
                  ))}
                </Collapsible>
              )}

              {/* Request Body */}
              {ep.requestBodyDiff && (
                <Collapsible title="Request Body Differences">
                  <p className="mb-2">{ep.requestBodyDiff.description}</p>
                  {ep.requestBodyDiff.changes && (
                    <ul className="list-disc list-inside text-sm text-yellow-800">
                      {ep.requestBodyDiff.changes.map((change, i) => (
                        <li key={i}>{change}</li>
                      ))}
                    </ul>
                  )}
                </Collapsible>
              )}

              {/* Response Body */}
              {ep.responseBodyDiff && (
                <Collapsible title="Response Body Differences">
                  <p className="mb-2">{ep.responseBodyDiff.description}</p>
                  {ep.responseBodyDiff.changes && (
                    <ul className="list-disc list-inside text-sm text-yellow-800">
                      {ep.responseBodyDiff.changes.map((change, i) => (
                        <li key={i}>{change}</li>
                      ))}
                    </ul>
                  )}
                </Collapsible>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
